import type {
  AnalyticsMeasurementCheckpoint,
  AnalyticsUsageDay
} from 'shared/services/analytics/AnalyticsTrackingDB'

import {
  ANALYTICS_CLOCK_DRIFT_TOLERANCE_MS,
  ANALYTICS_MAX_FOREGROUND_GAP_MS,
  ANALYTICS_MAX_USAGE_DAY_AGE_DAYS
} from './AnalyticsTracker.constants'
import type {
  AnalyticsClock,
  AnalyticsTimingState
} from './AnalyticsTracker.types'

const MS_PER_DAY = 24 * 60 * 60 * 1000

export const readAnalyticsClock = (): AnalyticsClock => ({
  wallTimeMs: Date.now(),
  monotonicTimeMs: performance.now()
})

export const getUtcDay = (timestamp: number) =>
  new Date(timestamp).toISOString().slice(0, 10)

export const getUtcDayStart = (timestamp: number) => {
  const date = new Date(timestamp)
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

export const getNextUtcDayStart = (timestamp: number) =>
  getUtcDayStart(timestamp) + MS_PER_DAY

export const isReportableUsageDay = (day: string, now: number) =>
  day >= getUtcDay(now - ANALYTICS_MAX_USAGE_DAY_AGE_DAYS * MS_PER_DAY) &&
  day <= getUtcDay(now)

const emptyDay = (
  usageDay: string,
  measurementId: string
): AnalyticsUsageDay => ({
  measurementId,
  usageDay,
  elapsedTimeMs: 0,
  foregroundTimeMs: 0
})

export const createTimingState = (
  clock: AnalyticsClock,
  foreground: boolean,
  measurementId: string
): AnalyticsTimingState => ({
  lastClock: clock,
  foreground,
  days: [emptyDay(getUtcDay(clock.wallTimeMs), measurementId)]
})

/**
 * Consume each interval once. Only short intervals with consistent clocks may
 * contribute foreground time. Date.now also catches sleep on platforms where
 * performance.now stops ticking; the monotonic clock catches wall-clock edits.
 * Elapsed/open-tab time deliberately keeps its independent wall-clock meaning.
 */
export const advanceTiming = (
  state: AnalyticsTimingState,
  clock: AnalyticsClock,
  foreground: boolean,
  createMeasurementId: () => string,
  acceptForegroundTail = true
) => {
  const previous = state.lastClock
  const wallDelta = clock.wallTimeMs - previous.wallTimeMs
  const monotonicDelta = clock.monotonicTimeMs - previous.monotonicTimeMs
  const canCountForeground =
    acceptForegroundTail &&
    state.foreground &&
    wallDelta > 0 &&
    monotonicDelta >= 0 &&
    wallDelta <= ANALYTICS_MAX_FOREGROUND_GAP_MS &&
    monotonicDelta <= ANALYTICS_MAX_FOREGROUND_GAP_MS &&
    Math.abs(wallDelta - monotonicDelta) <= ANALYTICS_CLOCK_DRIFT_TOLERANCE_MS

  state.lastClock = clock
  state.foreground = foreground
  state.days = state.days.filter(day =>
    isReportableUsageDay(day.usageDay, clock.wallTimeMs)
  )

  if (wallDelta <= 0 || monotonicDelta < 0) return

  const foregroundDelta = canCountForeground
    ? Math.min(wallDelta, monotonicDelta)
    : 0
  // A tab can resume weeks later. Bound both storage and work to the backend's
  // supported window instead of looping over every missed day.
  let cursor = Math.max(
    previous.wallTimeMs,
    getUtcDayStart(clock.wallTimeMs) -
      ANALYTICS_MAX_USAGE_DAY_AGE_DAYS * MS_PER_DAY
  )

  while (cursor < clock.wallTimeMs) {
    const end = Math.min(getNextUtcDayStart(cursor), clock.wallTimeMs)
    const usageDay = getUtcDay(cursor)
    let day = state.days.find(candidate => candidate.usageDay === usageDay)

    if (!day) {
      day = emptyDay(usageDay, createMeasurementId())
      state.days.push(day)
    }

    const elapsed = end - cursor
    day.elapsedTimeMs = Math.min(MS_PER_DAY, day.elapsedTimeMs + elapsed)
    day.foregroundTimeMs = Math.min(
      day.elapsedTimeMs,
      day.foregroundTimeMs + foregroundDelta * (elapsed / wallDelta)
    )
    cursor = end
  }
}

export const takeCompletedDays = (state: AnalyticsTimingState) => {
  const today = getUtcDay(state.lastClock.wallTimeMs)
  const completed = state.days.filter(day => day.usageDay < today)
  state.days = state.days.filter(day => day.usageDay >= today)
  return completed
}

/** Restore only recorded counters; never bridge the time the document was absent. */
export const restoreTimingDays = (
  state: AnalyticsTimingState,
  checkpoint: AnalyticsMeasurementCheckpoint
) => {
  for (const saved of checkpoint.days) {
    if (!isReportableUsageDay(saved.usageDay, state.lastClock.wallTimeMs))
      continue

    const current = state.days.find(day => day.usageDay === saved.usageDay)
    if (current) {
      current.measurementId = saved.measurementId
      current.elapsedTimeMs = Math.min(
        MS_PER_DAY,
        current.elapsedTimeMs + saved.elapsedTimeMs
      )
      current.foregroundTimeMs = Math.min(
        current.elapsedTimeMs,
        current.foregroundTimeMs + saved.foregroundTimeMs
      )
    } else {
      state.days.push({ ...saved })
    }
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isFiniteNonNegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

const isOptionalId = (value: unknown) =>
  value === undefined ||
  (typeof value === 'number' && Number.isSafeInteger(value))

const isUsageDay = (value: unknown): value is AnalyticsUsageDay => {
  if (!isRecord(value)) return false
  if (
    typeof value.measurementId !== 'string' ||
    !value.measurementId ||
    typeof value.usageDay !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(value.usageDay) ||
    !isFiniteNonNegative(value.elapsedTimeMs) ||
    !isFiniteNonNegative(value.foregroundTimeMs)
  )
    return false

  const dayStart = Date.parse(`${value.usageDay}T00:00:00.000Z`)
  return (
    Number.isFinite(dayStart) &&
    getUtcDay(dayStart) === value.usageDay &&
    value.elapsedTimeMs <= MS_PER_DAY &&
    value.foregroundTimeMs <= value.elapsedTimeMs
  )
}

// Browser storage can contain old versions or malformed values, even though
// IndexedDB's compile-time schema describes only the version we write today.
export const isMeasurementCheckpoint = (
  value: unknown
): value is AnalyticsMeasurementCheckpoint => {
  if (!isRecord(value)) return false
  return (
    value.version === 2 &&
    typeof value.measurementId === 'string' &&
    Boolean(value.measurementId) &&
    typeof value.path === 'string' &&
    typeof value.appId === 'string' &&
    typeof value.appName === 'string' &&
    typeof value.appPath === 'string' &&
    isOptionalId(value.userId) &&
    isOptionalId(value.organizationId) &&
    (value.originalOrganizationId === null ||
      isOptionalId(value.originalOrganizationId)) &&
    isFiniteNonNegative(value.createdAt) &&
    isFiniteNonNegative(value.updatedAt) &&
    value.updatedAt >= value.createdAt &&
    Array.isArray(value.days) &&
    value.days.length <= ANALYTICS_MAX_USAGE_DAY_AGE_DAYS + 1 &&
    value.days.every(isUsageDay) &&
    new Set(value.days.map(day => day.usageDay)).size === value.days.length &&
    new Set(value.days.map(day => day.measurementId)).size === value.days.length
  )
}
